import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useLinkClickHandler,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { NavigationMenuItemDto } from '@asset-metrix/portal-gateway-client-js';
import {
  AppShell,
  NavigationHeader,
  PageHeader,
  PageHeaderNavigation,
} from '@asset-metrix/portal-ui';
import { useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import { useLoginMode } from '../api/portalApi/loginMode';
import { fetchDataRefreshTime } from '../api/portalGateway/dataRefreshTime';
import { fetchNavigation } from '../api/portalGateway/navigation';
import { fetchPage, PageQueryKey } from '../api/portalGateway/page';
import { useTenantInfoData } from '../api/portalGateway/tenantInfo';
import { DataRefreshDateNavigationItem } from '../components/datarefresh/DataRefreshDateNavigationItem';
import { PageFilters } from '../components/filter/PageFilters';
import { Navigation } from '../components/navigation/Navigation';
import { NavigationContextProvider } from '../components/navigation/NavigationContext';
import { TopNavigation } from '../components/navigation/TopNavigation';
import { useNavigationCustomizationQuery } from '../components/navigation/useNavigationCustomizationQuery';
import { getNavigationItemBySlug } from '../components/page/getNavigationItemBySlug';
import { PageContentRenderer } from '../components/page/PageContentRenderer';
import { PageWithErrorBoundary } from '../components/page/PageWithErrorBoundary';
import { UserMenu } from '../components/usermenu/UserMenu';
import { ZipUploadButton } from '../components/zipUpload/ZipUploadButton';
import { useCreateDynamicNavItem } from '../hooks/useCreateDynamicNavItem';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useGetPageCacheKey } from '../hooks/useGetPageCacheKey';
import { usePresetPageFiltersFromUrl } from '../hooks/usePresetPageFiltersFromUrl';
import { usePresetTableFiltersFromUrl } from '../hooks/usePresetTableFiltersFromUrl';
import { useSyncPageFiltersToSearchParams } from '../hooks/useSyncPageFiltersToSearchParams';
import { useSyncTableFiltersToSearchParams } from '../hooks/useSyncTableFiltersToSearchParams';
import { useStore } from '../store';
import { pixelSizeHelperPortalUi } from '../utils/pixelSizeHelperPortalUi';
import { prependRoleId } from '../utils/prependRoleId';

type Props = {
  pageType: 'page' | 'detailPage';
};

export const Main = ({ pageType }: Props) => {
  const { t } = useTranslation();
  const { data: resNavigation, isFetched: resNavigationFetched } = useQuery({
    queryKey: prependRoleId(['navigation']),
    queryFn: fetchNavigation,
  });

  const { data: tenantInfoData } = useTenantInfoData();

  const [
    isNavigationCollapsed,
    logoutSession,
    setFilterLoaded,
    setNavigationCollapsed,
    userEmail,
  ] = useStore(
    useShallow((state) => [
      state.isNavigationCollapsed,
      state.logoutSession,
      state.setFilterLoaded,
      state.setNavigationCollapsed,
      state.session?.getUserEmail(),
    ])
  );

  const { data: loginModeData } = useLoginMode(userEmail);

  const navigate = useNavigate();
  const { '*': slug } = useParams();
  const [urlSearchParams] = useSearchParams();

  const searchSlug = slug?.replace(/^(.*)\/$/, '$1');

  const navigationData = searchSlug
    ? getNavigationItemBySlug(
        resNavigation?.data[
          pageType === 'detailPage'
            ? 'detailPageRootNavigationItems'
            : 'rootNavigationItems'
        ] ?? [],
        searchSlug
      )
    : undefined;

  const pageId =
    navigationData?.type === 'navigation-page-item'
      ? navigationData.pageId
      : null;

  const { data: resPage } = useQuery({
    queryKey: prependRoleId<PageQueryKey>(['page', pageId ?? 'unknown']),
    queryFn: fetchPage,

    enabled: Boolean(pageId),
  });

  const pageFilterConfig = resPage?.data.pageFilter;

  // create a dynamic navigation item
  const navItemId = useCreateDynamicNavItem(navigationData, resPage?.data);
  // set filter key for page filters
  // pages use <pageId>, details pages use <pageId>/<detailsDiscriminator>/<detailsValue>
  const instanceId = useGetPageCacheKey(pageType, pageId);
  // preset filters from url search params
  usePresetPageFiltersFromUrl({
    pageData: resPage?.data,
    // dynamic nav items use their own filter store
    instanceId,
  });
  usePresetTableFiltersFromUrl(resPage?.data);

  // use hook to sync back filters to url
  useSyncPageFiltersToSearchParams({
    config: pageFilterConfig,
    instanceId,
    navigationData,
    navItemId,
    pageData: resPage?.data,
  });

  useSyncTableFiltersToSearchParams({
    navigationData,
    navItemId,
    pageData: resPage?.data,
  });

  const { data: dataRefresh, isPending: isDataRefreshPending } = useQuery({
    queryKey: ['dataRefreshTime'],
    queryFn: fetchDataRefreshTime,
  });

  const logout = useCallback(async () => {
    await logoutSession({ initiatedByUser: true });
  }, [logoutSession]);

  const pageContent = resPage?.data.contents.map((content, index) =>
    PageContentRenderer({
      pageNode: content,
      Render: PageContentRenderer,
      pageId: pageId ?? 'unknown',
      instanceId: instanceId ?? 'unknown',
      child_key: `${pageId}-${index}`,
    })
  );

  useDocumentTitle(
    pageType === 'detailPage'
      ? resPage?.data.pageHeader?.title
      : navigationData?.completeSlug
  );

  useEffect(() => {
    document.body.classList.add('portal-scene--main');
    return () => document.body.classList.remove('portal-scene--main');
  }, []);

  useEffect(() => {
    if (
      instanceId != null &&
      resPage != null &&
      (resPage.data.pageFilter == null ||
        resPage.data.pageFilter.filters.length === 0)
    ) {
      setFilterLoaded(instanceId);
    }
  }, [resPage, setFilterLoaded, instanceId]);

  useEffect(() => {
    // navigate to initial page if navigation item could not be retrieved from url slug (unknown page)
    if (resNavigationFetched && resNavigation?.data && navigationData == null) {
      navigate(resNavigation.data.initialPageSlug, { replace: true });
    }
    if (navigationData?.type === 'navigation-menu-item') {
      // append current url search params when navigating to not lose details filters
      navigate(
        `${navigationData.pageSlug}${
          // we cannot use UrlSearchParams.size - jest doesn't support it and the tests fail
          urlSearchParams.toString().length > 0
            ? '?' + urlSearchParams.toString()
            : ''
        }`,
        {
          replace: true,
        }
      );
    }
  }, [
    navigationData,
    navigate,
    resNavigation,
    resNavigationFetched,
    urlSearchParams,
  ]);

  const { data: dynamicNavigationItems } = useNavigationCustomizationQuery();

  const currentDynamicNavigationItem =
    dynamicNavigationItems?.data.dynamicNavigationItems.find(
      (item) => item.id === navItemId
    );
  const navigateBackClickHandler = useLinkClickHandler<HTMLDivElement>(
    currentDynamicNavigationItem?.originHref ?? ''
  );

  const showPasswordChange =
    !loginModeData ||
    loginModeData.mode !== 'sso' ||
    loginModeData.changePasswordUrl;

  const goToPasswordChange = () => {
    if (loginModeData?.changePasswordUrl) {
      globalThis.location.assign(loginModeData?.changePasswordUrl);
      return;
    } else {
      navigate('/changePassword');
    }
  };

  const currentMainSlug = searchSlug?.split('/').shift();
  const currentNavigationTree = resNavigation?.data[
    pageType === 'page'
      ? 'rootNavigationItems'
      : 'detailPageRootNavigationItems'
  ].find((nav) => nav.slug === currentMainSlug) as
    | NavigationMenuItemDto
    | undefined;
  const hasSubnavigation =
    currentNavigationTree && currentNavigationTree?.items?.length > 0;

  const userMenuButton = (
    <UserMenu
      onLogout={logout}
      onChangePassword={showPasswordChange ? goToPasswordChange : undefined}
    />
  );

  const zipUploadButton = tenantInfoData?.enableGlobalZipUpload && (
    <ZipUploadButton />
  );

  return (
    <AppShell
      headerHeight={hasSubnavigation ? pixelSizeHelperPortalUi(50) : undefined}
      navigationHeader={
        <NavigationHeader
          collapsed={isNavigationCollapsed}
          onCollapseClick={() => setNavigationCollapsed(!isNavigationCollapsed)}
          userIcon={userMenuButton}
          zipUploadButton={zipUploadButton}
        />
      }
      navigation={
        <NavigationContextProvider>
          <Navigation
            userMenuButton={userMenuButton}
            isPending={isDataRefreshPending}
          />
        </NavigationContextProvider>
      }
      navigationFooter={
        dataRefresh?.timestamp && (
          <DataRefreshDateNavigationItem
            collapsed={isNavigationCollapsed}
            timestamp={dataRefresh.timestamp}
          />
        )
      }
      navigationCollapsed={isNavigationCollapsed}
      subNavigation={
        <TopNavigation
          pageType={pageType}
          navigationData={resNavigation}
          slug={slug}
          activeDynamicNavigationItem={currentDynamicNavigationItem}
        />
      }
      navigationWidthCollapsed={pixelSizeHelperPortalUi(64)}
      navigationWidthExpanded={pixelSizeHelperPortalUi(300)}
    >
      <PageWithErrorBoundary
        pageHeaderNavigation={
          currentDynamicNavigationItem?.originHref && (
            <PageHeaderNavigation
              label={t('app.backToPage')}
              onClick={navigateBackClickHandler}
            />
          )
        }
        pageHeader={
          navigationData?.label && (
            <PageHeader
              title={resPage?.data.pageHeader?.title || navigationData.label}
            />
          )
        }
        pageFilter={
          pageId &&
          instanceId &&
          Boolean(pageFilterConfig?.filters.length) && (
            <PageFilters
              config={pageFilterConfig}
              filterKey={instanceId}
              pageId={pageId}
              activeDynamicNavigationItem={currentDynamicNavigationItem}
            />
          )
        }
        pageContent={pageContent}
        headerHeight={
          hasSubnavigation ? pixelSizeHelperPortalUi(50) : undefined
        }
        slug={currentDynamicNavigationItem?.href ?? slug}
        activeDynamicNavigationItem={currentDynamicNavigationItem}
        hasVisiblePageFilters={Boolean(
          pageFilterConfig?.filters.filter((f) => !f.hidden).length
        )}
      />
    </AppShell>
  );
};
